import BeforeAfter from "@components/paywall/BeforeAfter"
import PaywallFaq from "@components/paywall/PaywallFaq"
import PaywallGuarantee from "@components/paywall/PaywallGuarantee"
import PaywallPlans from "@components/paywall/PaywallPlans"
import PaywallReviews from "@components/paywall/PaywallReviews"
import PaywallTimerBar from "@components/paywall/PaywallTimerBar"
import WhatYouGet from "@components/paywall/WhatYouGet"
import FunnelLayout from "@components/quiz/FunnelLayout"
import FunnelLogo from "@components/quiz/FunnelLogo"
import Spinner from "@components/Spinner"
import resolvePrice from "@helpers/pricing"
import useCountdown from "@hooks/useCountdown"
import { GetPricesData, GetPricesLoaderResult } from "@loaders/getPricesLoader"
import ErrorPage, { AsyncErrorPage } from "@pages/ErrorPage"
import getPersonalization, {
  toInlineLabel,
} from "@pages/onboarding/personalization"
import { Suspense, useMemo, useRef, useState } from "react"
import { Trans, useTranslation } from "react-i18next"
import { Await, useLoaderData } from "react-router-dom"
import { Plan } from "src/types"
import getTargetDateLabel from "./getTargetDate"

const COUNTDOWN_MINUTES = 10
const MONEY_BACK_DAYS = 14
const PLAN_ORDER: Plan[] = ["monthly", "quarterly", "yearly"]
const POPULAR_PLAN: Plan = "quarterly"

function getAvailablePlans({ currency, prices }: GetPricesData): Plan[] {
  return PLAN_ORDER.filter(
    (plan) =>
      resolvePrice({ currency, stripePrice: prices[plan].intro }) &&
      resolvePrice({ currency, stripePrice: prices[plan].full }),
  )
}

function Paywall({
  availablePlans,
  data,
}: {
  availablePlans: Plan[]
  data: GetPricesData
}) {
  const popularPlan = availablePlans.includes(POPULAR_PLAN)
    ? POPULAR_PLAN
    : availablePlans[availablePlans.length - 1]

  const [selectedPlan, setSelectedPlan] = useState<Plan>(popularPlan)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const plansRef = useRef<HTMLDivElement>(null)
  const { isExpired, timeLeft } = useCountdown(COUNTDOWN_MINUTES)
  const { t } = useTranslation()
  const personalization = useMemo(() => getPersonalization(t), [t])
  const targetDate = useMemo(getTargetDateLabel, [])

  const scrollToPlans = () =>
    plansRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })

  if (isSubmitting) {
    return (
      <div className="flex min-h-dvh items-center justify-center">
        <Spinner />
      </div>
    )
  }

  const plans = (
    <PaywallPlans
      currency={data.currency}
      isSubmitting={isSubmitting}
      onPlanChange={setSelectedPlan}
      plans={availablePlans}
      popularPlan={popularPlan}
      prices={data.prices}
      selectedPlan={selectedPlan}
      setIsSubmitting={setIsSubmitting}
    />
  )

  return (
    <>
      {isExpired ? (
        <header className="vm-container flex h-16 items-center justify-center">
          <FunnelLogo />
        </header>
      ) : (
        <PaywallTimerBar onGetPlan={scrollToPlans} timeLeft={timeLeft} />
      )}

      <div className="vm-container space-y-10 pb-16 pt-6">
        <section className="scroll-mt-24" ref={plansRef}>
          {plans}
        </section>

        <section className="space-y-5">
          <h1 className="vm-heading">
            <Trans
              components={{ colored: <span className="vm-highlight" /> }}
              i18nKey="pages.checkout.paywall.heading"
              values={{ goal: toInlineLabel(personalization.goalLabel) }}
            />
          </h1>

          <BeforeAfter />

          <div className="grid grid-cols-2 gap-3">
            <div className="vm-card p-3">
              <p className="text-xs text-vm-muted">
                {t("pages.checkout.paywall.summary.goal")}
              </p>
              <p className="mt-0.5 text-sm font-semibold text-vm-ink">
                {personalization.goalLabel}
              </p>
            </div>
            <div className="vm-card p-3">
              <p className="text-xs text-vm-muted">
                {t("pages.checkout.paywall.summary.goalDate")}
              </p>
              <p className="mt-0.5 text-sm font-semibold text-vm-ink">
                {targetDate}
              </p>
            </div>
          </div>
        </section>

        <section>
          <WhatYouGet />
        </section>

        <section>
          <PaywallReviews />
        </section>

        <section>
          <PaywallGuarantee days={MONEY_BACK_DAYS} />
        </section>

        <section>
          <PaywallFaq />
        </section>

        <section>{plans}</section>
      </div>
    </>
  )
}

function NoPlansErrorPage({ currency }: { currency: string }) {
  // NOTE: Created once, so re-renders don't report it again.
  const [error] = useState(
    () => new Error(`No plan has both Stripe prices in ${currency}`),
  )

  return <ErrorPage error={error} />
}

export default function PlansPage() {
  const data = useLoaderData() as GetPricesLoaderResult

  return (
    <FunnelLayout>
      <Suspense
        fallback={
          <div className="flex grow items-center justify-center">
            <Spinner />
          </div>
        }
      >
        <Await errorElement={<AsyncErrorPage />} resolve={data.getPricesData}>
          {(getPricesData: GetPricesData) => {
            const availablePlans = getAvailablePlans(getPricesData)

            return availablePlans.length > 0 ? (
              <Paywall availablePlans={availablePlans} data={getPricesData} />
            ) : (
              <NoPlansErrorPage currency={getPricesData.currency} />
            )
          }}
        </Await>
      </Suspense>
    </FunnelLayout>
  )
}
